import { cn } from "@/utils/theme";
import {
  Icon,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@elaniintc/goes-design-system";
import clsx from "clsx";
import { useEffect, type ReactNode } from "react";
import { Renderify } from "renderify-js";
import { useIntersectionObserver } from "usehooks-ts";

export interface SelectOption {
  value: string;
  label: string;
  icon?: ReactNode;
  avatar?: ReactNode;
  iconPosition?: string; // left | right
  indicator?: ReactNode;
}
interface SelectInputProps {
  name: string;
  placeholder: string;
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  hasMore?: boolean;
  showIndicator?: boolean;
  optionTextClassName?: string;
  triggerClassName?: string;
  group?:
    | "down"
    | "down-left"
    | "down-right"
    | "left"
    | "right"
    | "up"
    | "up-left"
    | "up-right"
    | "single"
    | "middle"
    | "middle-right"
    | "middle-left"
    | "middle-up";
  ref?: (node?: Element | null | undefined) => void;
  shouldCircleIcon?: boolean;
  id?: string;
  emptyStateMessage?: string;
}

export const SelectInput = ({
  onChange,
  value,
  name,
  options,
  isLoading = false,
  isDisabled = false,
  placeholder,
  hasMore = false,
  showIndicator = false,
  optionTextClassName,
  group,
  triggerClassName,
  ref,
  shouldCircleIcon,
  id,
  emptyStateMessage,
}: SelectInputProps) => {
  return (
    <Select onValueChange={onChange} value={value} name={name}>
      <SelectTrigger
        className={cn(
          value ? "min-w-0" : "text-body-quiet",
          "w-full",
          triggerClassName,
        )}
        isLoading={isLoading}
        disabled={isDisabled}
        id={id || name}
        group={group}
      >
        {value ? (
          <div className="flex items-center gap-2 truncate">
            {(() => {
              const selectedOption = options.find(
                (option) => option.value === value,
              );
              if (!selectedOption) return null;
              return (
                <>
                  {selectedOption.iconPosition?.toLowerCase() === "left" && (
                    <div
                      className={clsx({
                        "px-1.5 py-1 rounded-full border border-neutral text-sm bg-neutral-object-accent-silent":
                          shouldCircleIcon,
                      })}
                    >
                      {selectedOption.icon}
                    </div>
                  )}
                  <p className="truncate">{selectedOption.label || ""}</p>
                  {selectedOption.iconPosition?.toLowerCase() === "right" && (
                    <div
                      className={clsx({
                        "px-1.5 py-1 rounded-full border border-neutral text-sm bg-neutral-object-accent-silent":
                          shouldCircleIcon,
                      })}
                    >
                      {selectedOption.icon}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        ) : (
          <p className="truncate text-body-quiet">{placeholder}</p>
        )}
      </SelectTrigger>
      <SelectContent className="max-h-85 max-w-(--radix-select-trigger-width) overflow-y-auto border border-neutral py-2">
        {options.map(({ label, value, avatar, icon, iconPosition }) => {
          return (
            <SelectItem
              showIndicator={showIndicator}
              key={value}
              value={value}
              className={cn("w-full flex", optionTextClassName)}
            >
              <div className="flex-1 w-full flex items-center justify-between gap-2">
                {iconPosition?.toLowerCase() === "left" && icon}
                {avatar}
                <p className="truncate pr-2 flex-1">{label}</p>
                {iconPosition?.toLowerCase() === "right" && icon}
              </div>
            </SelectItem>
          );
        })}

        {hasMore && <div ref={ref} className="w-full py-2" />}
        {!isLoading && options.length === 0 && emptyStateMessage && (
          <div className="h-20 flex items-center justify-center">
            <p className="text-body-quiet text-sm text-center">
              {emptyStateMessage}
            </p>
          </div>
        )}
        {isLoading && (
          <div className="flex items-center justify-center py-2">
            <Icon
              type="far"
              name="spinner-third"
              className="animate-spin text-on-disabled"
            />
          </div>
        )}
      </SelectContent>
    </Select>
  );
};
interface Props {
  name: string;
  label: string;
  required?: boolean;
  placeholder: string;
  value: string;
  options: SelectOption[];
  error?: string;
  onChange: (value: string) => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  showIndicator?: boolean;
  optionTextClassName?: string;
  srOnlyLabel?: boolean; // Add this line
  fullWidth?: boolean;
  triggerClassName?: string;
  shouldCircleIcon?: boolean;
  id?: string;
  emptyStateMessage?: string;
}

const SelectField = ({
  onChange,
  value,
  id,
  name,
  label,
  required,
  options,
  isLoading = false,
  isDisabled = false,
  placeholder,
  hasMore = false,
  error,
  onLoadMore,
  showIndicator = false,
  optionTextClassName,
  srOnlyLabel,
  fullWidth = false,
  triggerClassName,
  shouldCircleIcon,
  emptyStateMessage,
}: Props) => {
  const [ref, isIntercepting, entry] = useIntersectionObserver({});

  useEffect(() => {
    if (isIntercepting && !isLoading && entry?.isIntersecting && hasMore) {
      // Call the onLoadMore function if it exists
      if (typeof onLoadMore === "function") {
        onLoadMore();
      }
    }
  }, [isIntercepting, isLoading, onLoadMore, entry, hasMore]);

  return (
    <div className={cn("flex flex-col gap-1", fullWidth && "w-full")}>
      <Renderify condition={!!label}>
        <label
          className={cn(
            "font-semibold text-body-loud text-sm leading-2xl",
            isDisabled && "text-on-disabled!",
            srOnlyLabel && "sr-only",
          )}
          htmlFor={id || name}
        >
          {label}{" "}
          {required && (
            <span
              className={cn(
                "text-sm text-danger",
                isDisabled && "text-on-disabled!",
              )}
            >
              *
            </span>
          )}
        </label>
      </Renderify>
      <SelectInput
        id={id || name}
        name={name}
        placeholder={placeholder}
        value={value}
        options={options}
        isLoading={isLoading}
        isDisabled={isDisabled}
        showIndicator={showIndicator}
        optionTextClassName={optionTextClassName}
        triggerClassName={triggerClassName}
        hasMore={hasMore}
        onChange={onChange}
        shouldCircleIcon={shouldCircleIcon}
        emptyStateMessage={emptyStateMessage}
        ref={ref}
      />
      {error && (
        <p className="text-danger text-xs leading-xl font-normal">
          <Icon type="far" name="circle-xmark" className="mr-1.5" />
          {error}
        </p>
      )}
    </div>
  );
};

export default SelectField;
